import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Box,
    Alert,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useUpdateOrderStatus } from '../../../hooks/orders/useUpdateOrderStatus';
import { ORDER_STATUS_OPTIONS } from '../Table/OrderStatusBadge';
import FormInput from '../../inputs/FormInput';

// Allowed forward transitions per status. Backward / lateral transitions
// are disallowed by the backend; we hide them in the UI per the spec.
const ALLOWED_TRANSITIONS = {
    pending:    ['confirmed', 'cancelled'],
    confirmed:  ['processing', 'shipped', 'cancelled'],
    processing: ['shipped', 'cancelled'],
    shipped:    ['delivered'],
    delivered:  [],
    cancelled:  [],
    refunded:   [],
    partially_refunded: [],
    failed:     [],
};

// NOTE: The status dropdown stays as a raw MUI TextField select — the shared
// FormInput's `select` type is wired to SelectAutoComplacent (which expects
// FK-style {id,name} options), not enum dropdowns like this one.
const UpdateStatusDialog = ({ open, order, onClose }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const [status, setStatus] = useState('');
    const [comment, setComment] = useState('');
    const [trackingNumber, setTrackingNumber] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const { updateOrderStatusMutate, isUpdatingStatus } = useUpdateOrderStatus();

    useEffect(() => {
        if (open) {
            setStatus('');
            setComment('');
            setTrackingNumber('');
            setFieldErrors({});
        }
    }, [open]);

    const allowed = ALLOWED_TRANSITIONS[order?.status] || [];
    const options = ORDER_STATUS_OPTIONS.filter((o) => allowed.includes(o.value));
    const requiresTracking = status === 'shipped';

    const submit = () => {
        if (!status) return;

        if (requiresTracking && !trackingNumber.trim()) {
            setFieldErrors({
                tracking_number: isArabic
                    ? 'رقم التتبع مطلوب عند شحن الطلب.'
                    : 'Tracking number is required when shipping the order.',
            });
            return;
        }

        const payload = {
            status,
            comment: comment || undefined,
        };
        if (requiresTracking) payload.tracking_number = trackingNumber.trim();

        updateOrderStatusMutate(
            { id: order.id, data: payload },
            {
                onSuccess: () => onClose?.(),
                onError: (err) => {
                    const apiErrors = err?.response?.data?.errors;
                    if (apiErrors && typeof apiErrors === 'object') {
                        const flat = {};
                        Object.keys(apiErrors).forEach((k) => {
                            flat[k] = Array.isArray(apiErrors[k])
                                ? apiErrors[k][0]
                                : apiErrors[k];
                        });
                        setFieldErrors(flat);
                    }
                },
            }
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isArabic ? 'تحديث حالة الطلب' : 'Update Order Status'}
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        select
                        label={isArabic ? 'الحالة الجديدة' : 'New Status'}
                        value={status}
                        onChange={(e) => {
                            setStatus(e.target.value);
                            setFieldErrors({});
                        }}
                        fullWidth
                        required
                        helperText={
                            options.length === 0
                                ? (isArabic
                                    ? 'لا توجد انتقالات متاحة من الحالة الحالية.'
                                    : 'No transitions available from the current status.')
                                : ''
                        }
                    >
                        {options.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {isArabic ? opt.label.ar : opt.label.en}
                            </MenuItem>
                        ))}
                    </TextField>

                    {requiresTracking && (
                        <FormInput
                            type="text"
                            name="tracking_number"
                            label={isArabic ? 'رقم التتبع' : 'Tracking Number'}
                            placeholder={isArabic ? 'مثال: 1Z999AA10123456784' : 'e.g. 1Z999AA10123456784'}
                            value={trackingNumber}
                            onChange={(e) => {
                                setTrackingNumber(e.target.value);
                                if (fieldErrors.tracking_number) {
                                    setFieldErrors((prev) => ({ ...prev, tracking_number: undefined }));
                                }
                            }}
                            required
                            error={fieldErrors.tracking_number}
                        />
                    )}

                    <FormInput
                        type="textarea"
                        name="comment"
                        label={isArabic ? 'تعليق (اختياري)' : 'Comment (optional)'}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        required={false}
                        textareaRows="100px"
                        placeholder=""
                    />

                    {requiresTracking && (
                        <Alert severity="info" sx={{ mt: 0 }}>
                            <Typography variant="body2">
                                {isArabic
                                    ? 'سيتم إرسال بريد إلكتروني للعميل يحتوي على رقم التتبع.'
                                    : 'An email will be sent to the customer with the tracking number.'}
                            </Typography>
                        </Alert>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={isUpdatingStatus}>
                    {isArabic ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                    variant="contained"
                    onClick={submit}
                    disabled={isUpdatingStatus || !status}
                >
                    {isUpdatingStatus
                        ? (isArabic ? 'جاري...' : 'Working...')
                        : (isArabic ? 'تحديث' : 'Update')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UpdateStatusDialog;
